import React, {useState, useEffect, useRef} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Menu from "../../../../components/layout/menu";
import SaveButton from "../../../../components/forms/saveButton"
import CancelButton from "../../../../components/forms/cancelButton"
import { createNews } from "../../../../client/news";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ModalSuccess from "../../../../components/shared/modals/modalSuccess";
import ModalAlertWarning from "../../../../components/shared/modals/modalAlertWarning";
import { useBrokenImages } from "../../../../hooks/useBrokenImages";
import { validateImageFile as checkImageFile } from "../../../../utils/imageValidation";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import bannerDefault from "../../../../assets/images/news/banner-default.png"

const CustomPrevArrow = ({ onClick, currentSlide }) => (
  <button 
    onClick={onClick}
    className={`absolute z-10 transform rotate-180 -translate-y-1/2 cursor-pointer -left-3 sm:-left-2 md:-left-3 lg:-left-4 xl:-left-6 top-1/2 ${
      currentSlide === 0 ? 'opacity-0 pointer-events-none' : 'opacity-100'
    } transition-opacity duration-300`}
  >
    <svg className="w-4 lg:w-5 xl:w-6" viewBox="0 0 28 31" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M27 13.7679C28.3333 14.5377 28.3333 16.4622 27 17.232L3.75 30.6554C2.41667 31.4252 0.749998 30.463 0.749999 28.9234L0.75 2.0766C0.75 0.537005 2.41667 -0.425246 3.75 0.344555L27 13.7679Z" fill="#007696"/>
    </svg>
  </button>
);

const CustomNextArrow = ({ onClick, currentSlide, slideCount, slidesToShow }) => {
  const isLastSlide = currentSlide >= slideCount - slidesToShow;
  
  return (
    <button 
      onClick={onClick}
      className={`absolute -right-3 sm:-right-2 md:-right-3 lg:-right-4 xl:-right-6 top-1/2 transform -translate-y-1/2 z-10 cursor-pointer ${
        isLastSlide ? 'opacity-30 pointer-events-none' : 'opacity-100'
      } transition-opacity duration-300`}
    >
      <svg className="w-4 lg:w-5 xl:w-6" viewBox="0 0 28 31" fill="none">
        <path d="M27 13.7679C28.3333 14.5377 28.3333 16.4622 27 17.232L3.75 30.6554C2.41667 31.4252 0.749998 30.463 0.749999 28.9234L0.75 2.0766C0.75 0.537005 2.41667 -0.425246 3.75 0.344555L27 13.7679Z" fill="#007696"/>
      </svg>
    </button>
  );
};

export default function CreateNewsDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const { newsData, isCreating } = location.state || {};
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [title, setTitle] = useState(newsData?.title || "");
  const [extract, setExtract] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [content, setContent] = useState("");
  const [imageBlogFile, setImageBlogFile] = useState(null);
  const [imageBlogPreview, setImageBlogPreview] = useState(null);
  const { hasImage, markBroken } = useBrokenImages();
  const [galleryImages, setGalleryImages] = useState([null, null, null, null, null]);
  const [galleryFiles, setGalleryFiles] = useState([null, null, null, null, null]);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(3);
  const sliderRef = useRef(null);

  const extractQuillRef = useRef(null);
  const contentQuillRef = useRef(null);

  const [showModalAlertWarning, setShowModalAlertWarning] = useState(false);
  const [showModalSuccess, setShowModalSuccess] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const [imageBlogDimensions, setImageBlogDimensions] = useState({ width: 0, height: 0 });


  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  
  useEffect(() => {
    if (isCreating && newsData?.title) {
      setTitle(newsData.title);
    }
  }, [isCreating, newsData]);

  const getSlickSettings = () => ({
    dots: false,
    infinite: false,
    speed: 300,
    slidesToShow: slidesToShow,
    slidesToScroll: slidesToShow,
    beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex),
    prevArrow: <CustomPrevArrow currentSlide={currentSlide} />,
    nextArrow: <CustomNextArrow 
      currentSlide={currentSlide} 
      slideCount={Math.max(galleryImages.length, 5)}
      slidesToShow={slidesToShow}
    />,
    responsive: [
      {
        breakpoint: 1570,
        settings: {
          slidesToShow: Math.min(slidesToShow, 2),
          slidesToScroll: Math.min(slidesToShow, 2),
        }
      },
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: Math.min(slidesToShow, 2),
          slidesToScroll: Math.min(slidesToShow, 2),
        }
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: Math.min(slidesToShow, 2),
          slidesToScroll: Math.min(slidesToShow, 2),
        }
      },
      {
        breakpoint: 837,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  });

  useEffect(() => {
    const calculateSlidesToShow = () => {
      const screenWidth = window.innerWidth;
      
      let availableWidth = screenWidth;
      if (isMenuOpen) {
        if (screenWidth >= 1280) {
          availableWidth = screenWidth * 0.793; // 100% - 20.7%
        } else if (screenWidth >= 1024) {
          availableWidth = screenWidth * 0.763; // 100% - 23.7%
        }
      }
      
      if (availableWidth < 837) {
        setSlidesToShow(1);
      } else if (availableWidth < 1255) {
        setSlidesToShow(2);
      } else if (availableWidth < 1673) {
        setSlidesToShow(3);
      } else if (availableWidth < 2091){
        setSlidesToShow(4);
      } else {
        setSlidesToShow(5);
      }
    };

    calculateSlidesToShow();

    const handleResize = () => {
      calculateSlidesToShow();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuOpen]);

const validateImageFile = async (file) => {
  const { isValid, message } = await checkImageFile(file);
  if (!isValid) {
    setModalMessage(message);
    setShowModalAlertWarning(true);
  }
  return isValid;
};

  const handleImageBlogChange = async (file) => {
  if (await validateImageFile(file)) {
    setImageBlogFile(file);
    setImageBlogPreview(URL.createObjectURL(file));

    const img = new Image();
    img.onload = () => {
      setImageBlogDimensions({
        width: img.width,
        height: img.height
      });
    };
    img.src = URL.createObjectURL(file);
  }
  };


  const handleGalleryImageChange = async (index, file) => {
    if (!(await validateImageFile(file))) return;

    // Actualizaciones funcionales: la validación es asíncrona y puede haber otras subidas en curso
    setGalleryImages(prev => {
      const newImages = [...prev];
      newImages[index] = URL.createObjectURL(file);
      return newImages;
    });
    setGalleryFiles(prev => {
      const newFiles = [...prev];
      newFiles[index] = file;
      return newFiles;
    });
  };

  const handleSave = async () => {
    try {

      if (subtitle.length > 50) {
        setModalMessage("El subtítulo no puede exceder los 50 caracteres.");
        setShowModalAlertWarning(true);
        return;
      }

      setIsLoading(true);

      const formData = new FormData();
      
      formData.append('title', title);
      formData.append('advance', newsData?.advance || "");
      formData.append('extract', extract);
      formData.append('subtitle', subtitle);
      formData.append('content', content);
      formData.append('module', 'KnowUs');
      
      if (newsData?.imageListFile) {
        formData.append('imageListFile', newsData.imageListFile);
      }
      
      formData.append('imageBlogFile', imageBlogFile);
      
      galleryFiles.forEach((file, index) => {
        if (file) {
          formData.append('additionalImages', file);
        }
      });

      const response = await createNews(formData);
      
      if (response.data && response.data.news) {
        setModalMessage("Noticia creada correctamente");
        setShowModalSuccess(true);
        setTimeout(() => {
          navigate('/conocenos', { 
            state: { 
              message: "Noticia creada correctamente",
              showSuccess: true
            } 
          });
        }, 1500);
      }
    } catch (error) {
      console.error("Error creating news:", error);
      const errorMsg = error.response?.data?.message || "Error al crear la noticia";
      setError(errorMsg);
      setModalMessage(errorMsg);
      setShowModalAlertWarning(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/conocenos');
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3,false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['clean']
    ]
  };

  const quillFormats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
  ];



  return (
    <div className="min-h-screen">
      <Menu isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} className="transition-all duration-300" />
      <div className={`flex-1 transition-all duration-300 pt-[67px]  bg-[#F7F8F9] w-full z-50 min-h-screen ${isMenuOpen ? "xl:pl-[20.7%] lg:pl-[23.7%] pl-0" : "pl-0"}`}>
        <div className="sm:pt-8 pt-6 pb-10 px-[5%] sm:px-[3%]">
            <div className="flex flex-col gap-3">
                <p className="text-lg items-center font-itcbook flex gap-1 text-[#24325A]">
                    <button 
                        onClick={() => navigate('/conocenos')} 
                        className="hover:underline"
                    >
                        Noticias
                    </button>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4.83008 2.55301L9.38365 7.10658L4.83008 11.6602" stroke="#EF6224" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    {title || "Nueva noticia"}
                </p>
                
                {hasImage(imageBlogPreview) ? (
                  <div className="relative w-full overflow-hidden rounded-2xl" style={{ aspectRatio: '1078/482', backgroundColor: '#f3f4f6' }}>
                      <img 
                        src={imageBlogPreview}
                        onError={() => markBroken(imageBlogPreview)}
                        className="object-contain w-full h-full"
                      />
                        <div className="absolute inset-0 z-0 flex items-center justify-center bg-black opacity-50 rounded-2xl"></div>
                        <div className="absolute inset-0 z-20 flex items-center justify-center">
                          <label className="w-[50%] h-[30%] lg:w-[30%] p-1 md:p-2 rounded-[15px] border-dashed border-[#A4A4A4] lg:h-[112px] border-[1px] cursor-pointer">
                              <input
                                type="file"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  e.target.value = '';
                                  if (file) handleImageBlogChange(file);
                                }}
                                className="hidden"
                                accept=".jpg,.jpeg,.png,.webp,.svg"
                              />
                              <div className="flex flex-col items-center justify-center h-full gap-2">
                                      <svg width="49" height="49" viewBox="0 0 49 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <g clip-path="url(#clip0_3789_81138)">
                                      <path d="M24.4969 48.9935C18.0066 48.9935 11.5164 48.9986 5.0261 48.9901C2.5322 48.9866 0.573923 47.2337 0.0828183 44.595C0.0398466 44.3684 0.0137566 44.1316 0.0137566 43.8999C0.00608312 41.0312 -0.00926391 38.1625 0.00761782 35.2938C0.0152913 34.0962 0.721255 33.1269 1.73262 32.7913C2.72558 32.4608 3.76304 32.8407 4.3938 33.7589C4.77134 34.3091 4.88337 34.936 4.88183 35.6157C4.87416 38.0381 4.89411 40.4605 4.86802 42.8829C4.86188 43.423 5.00921 43.5507 5.48037 43.5507C18.1539 43.5371 30.8291 43.5354 43.5026 43.5541C44.0367 43.5541 44.1257 43.3599 44.1211 42.8267C44.0996 40.3192 44.0996 37.8116 44.1211 35.304C44.1319 34.1337 44.8117 33.161 45.7801 32.8083C46.7623 32.4506 47.8182 32.7998 48.4659 33.6993C48.825 34.1984 48.9923 34.7725 48.9923 35.413C48.9892 38.154 49.023 40.8966 48.98 43.6376C48.9324 46.6784 46.8222 48.9645 44.0843 48.9867C41.2896 49.0088 38.4934 48.9918 35.6987 48.9918C31.9647 48.9918 28.2323 48.9918 24.4984 48.9918L24.4969 48.9935Z" fill="white"/>
                                      <path d="M22.0527 8.73672C20.604 10.1421 19.2795 11.4283 17.9566 12.711C17.4087 13.2425 16.87 13.7842 16.3099 14.2987C15.2356 15.2885 13.7408 15.1794 12.8553 14.0653C11.959 12.9376 12.0234 11.2647 13.0716 10.2375C16.3436 7.03321 19.6294 3.84763 22.926 0.675676C23.8667 -0.230596 25.1298 -0.223782 26.0783 0.687601C29.3748 3.85955 32.6606 7.04513 35.9295 10.2529C36.9731 11.2767 37.0222 12.9734 36.1198 14.0841C35.222 15.1896 33.7241 15.2987 32.6637 14.2868C30.9233 12.6242 29.2044 10.9343 27.4733 9.2597C27.3336 9.12342 27.2201 8.94114 27.0067 8.89004C26.8656 9.12172 26.9392 9.38236 26.9392 9.62255C26.9346 18.1197 26.9362 26.6168 26.9331 35.114C26.9331 36.78 26.1504 37.8584 24.7998 38.0832C23.4923 38.3013 22.2031 37.1957 22.0742 35.7341C22.0466 35.4172 22.0527 35.097 22.0527 34.7784C22.0527 26.409 22.0527 18.0396 22.0527 9.66855C22.0527 9.41983 22.0527 9.17112 22.0527 8.73672Z" fill="white"/>
                                      </g>
                                      <defs>
                                      <clipPath id="clip0_3789_81138">
                                      <rect width="49" height="49" fill="white"/>
                                      </clipPath>
                                      </defs>
                                      </svg>
                                      <p className="text-xs text-white font-itcdemi">Cambiar foto</p>
                                      <p className="text-xs text-white font-itcdemi">Medidas subidas {imageBlogDimensions.width} x {imageBlogDimensions.height} px</p>
                              </div>
                          </label>
                        </div>
                    </div>
                    ) :(
                    <>
                    <div className="relative w-full overflow-hidden rounded-2xl" style={{ aspectRatio: '1078/482' }}>
                      <img src={bannerDefault} 
                         className="object-cover w-full h-full"
                      />
                      <div className="absolute inset-0 z-0 flex items-center justify-center bg-black opacity-50 rounded-2xl"></div>
                      <div className="absolute inset-0 z-20 flex items-center justify-center ">
                        <label className="w-[50%] h-[30%] lg:w-[30%] p-1 md:p-2 rounded-[15px] border-dashed border-[#A4A4A4] lg:h-[112px] border-[1px] cursor-pointer">
                              <input
                                type="file"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  e.target.value = '';
                                  if (file) handleImageBlogChange(file);
                                }}
                                className="hidden"
                                accept=".jpg,.jpeg,.png,.webp,.svg"
                              />
                              <div className="flex flex-col items-center justify-center h-full gap-2">
                                <svg width="49" height="49" viewBox="0 0 49 49" fill="white" xmlns="http://www.w3.org/2000/svg">
                                  <g clip-path="url(#clip0_3789_81138)">
                                  <path d="M24.4969 48.9935C18.0066 48.9935 11.5164 48.9986 5.0261 48.9901C2.5322 48.9866 0.573923 47.2337 0.0828183 44.595C0.0398466 44.3684 0.0137566 44.1316 0.0137566 43.8999C0.00608312 41.0312 -0.00926391 38.1625 0.00761782 35.2938C0.0152913 34.0962 0.721255 33.1269 1.73262 32.7913C2.72558 32.4608 3.76304 32.8407 4.3938 33.7589C4.77134 34.3091 4.88337 34.936 4.88183 35.6157C4.87416 38.0381 4.89411 40.4605 4.86802 42.8829C4.86188 43.423 5.00921 43.5507 5.48037 43.5507C18.1539 43.5371 30.8291 43.5354 43.5026 43.5541C44.0367 43.5541 44.1257 43.3599 44.1211 42.8267C44.0996 40.3192 44.0996 37.8116 44.1211 35.304C44.1319 34.1337 44.8117 33.161 45.7801 32.8083C46.7623 32.4506 47.8182 32.7998 48.4659 33.6993C48.825 34.1984 48.9923 34.7725 48.9923 35.413C48.9892 38.154 49.023 40.8966 48.98 43.6376C48.9324 46.6784 46.8222 48.9645 44.0843 48.9867C41.2896 49.0088 38.4934 48.9918 35.6987 48.9918C31.9647 48.9918 28.2323 48.9918 24.4984 48.9918L24.4969 48.9935Z" fill="white"/>
                                  <path d="M22.0527 8.73672C20.604 10.1421 19.2795 11.4283 17.9566 12.711C17.4087 13.2425 16.87 13.7842 16.3099 14.2987C15.2356 15.2885 13.7408 15.1794 12.8553 14.0653C11.959 12.9376 12.0234 11.2647 13.0716 10.2375C16.3436 7.03321 19.6294 3.84763 22.926 0.675676C23.8667 -0.230596 25.1298 -0.223782 26.0783 0.687601C29.3748 3.85955 32.6606 7.04513 35.9295 10.2529C36.9731 11.2767 37.0222 12.9734 36.1198 14.0841C35.222 15.1896 33.7241 15.2987 32.6637 14.2868C30.9233 12.6242 29.2044 10.9343 27.4733 9.2597C27.3336 9.12342 27.2201 8.94114 27.0067 8.89004C26.8656 9.12172 26.9392 9.38236 26.9392 9.62255C26.9346 18.1197 26.9362 26.6168 26.9331 35.114C26.9331 36.78 26.1504 37.8584 24.7998 38.0832C23.4923 38.3013 22.2031 37.1957 22.0742 35.7341C22.0466 35.4172 22.0527 35.097 22.0527 34.7784C22.0527 26.409 22.0527 18.0396 22.0527 9.66855C22.0527 9.41983 22.0527 9.17112 22.0527 8.73672Z" fill="white"/>
                                  </g>
                                  <defs>
                                  <clipPath id="clip0_3789_81138">
                                  <rect width="49" height="49" fill="white"/>
                                  </clipPath>
                                  </defs>
                                </svg>
                                <p className="text-xs text-white font-itcdemi">Subir foto</p>
                                <p className="text-xs text-center text-white font-itcdemi">
                                  Formatos permitidos: JPG, PNG, WEBP, SVG <br />
                                  Peso máx. 50 MB · Medidas ideales 1078 x 482 px
                                </p>

                              </div>
                        </label>
                      </div>
                    </div>
                    </>
                    ) }

                
                <div className="flex items-center justify-between gap-2 ">
                    <div className="pt-1 px-2 flex items-center bg-[#BBBFCC] rounded-lg  ">
                        <p className="text-sm font-anekmedium">
                          {new Date().toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}                        </p>  
                    </div>
                    <div className="pt-1 px-2 flex items-center bg-[#EFB86A] rounded-lg ">
                       <p className="text-sm text-white font-anekmedium">Noticias</p>
                    </div>
                </div>
                <h1 className="text-blue font-itcdemi text-2xl md:text-[32px]">
                    {title}
                </h1>

                <style>
                  {`
                    .ql-container {
                      border-bottom-left-radius: 10px !important;
                      border-bottom-right-radius: 10px !important;
                      height: 200px !important;
                    }
                    .ql-toolbar {
                      border-top-right-radius: 10px !important;
                      border-top-left-radius: 10px !important;
                      border-color:#D9D9D9;
                    }
                    .ql-toolbar .ql-snow{
                        border: 1px solid #D9D9D9;
                    }
                  `}
                </style>

                <ReactQuill
                  ref={extractQuillRef}
                  value={extract}
                  onChange={setExtract}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Escribe el extracto aquí"
                  className="overflow-visible bg-transparent outline-none"
                  style={{ overflow: 'visible' }} 
                />

                <input 
                    value={subtitle}
                    onChange={(e) => {
                        if (e.target.value.length <= 50) {
                            setSubtitle(e.target.value);
                        }
                    }}
                    maxLength={50}
                    className="text-blue border-[#D9D9D9] placeholder:font-itcdemi outline-none placeholder:text-2xl md:placeholder:text-[32px] border-[1px] rounded-xl px-3 py-2 font-itcdemi text-2xl md:text-[32px] bg-transparent"
                    placeholder="Escriba el subtítulo aquí"
                />
                <ReactQuill
                  ref={contentQuillRef}
                  value={content}
                  onChange={setContent}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Escribe el contenido aquí"
                  className="overflow-visible bg-transparent outline-none"
                  style={{ 
                    overflow: 'visible',
                  }} 
                />
                
                <h2 className="text-[#24325A] text-2xl md:text-[32px] font-itcdemi">
                    Galería de fotos
                </h2>
                <div className="relative">
                  <Slider 
                    ref={sliderRef}
                    {...getSlickSettings()}
                    className="gallery-slider"
                  >
                    {galleryImages.map((image, index) => (
                      <div key={index} className="px-2">
                        <div className="flex flex-col items-center justify-center rounded-2xl">
                          {hasImage(image) ? (
                            <>
                            <div className="relative">
                                <img 
                                  src={image} 
                                  onError={() => markBroken(image)}
                                  alt={`Gallery image ${index + 1}`} 
                                  className="object-cover w-[378px] h-[269px] rounded-2xl"
                                />
                                <label className="absolute flex items-center justify-center p-2 text-white rounded-full cursor-pointer bottom-2 right-2 bg-blue">
                                  <input
                                    type="file"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      e.target.value = '';
                                      if (file) handleGalleryImageChange(index, file);
                                    }}
                                    className="hidden"
                                    accept=".jpg,.jpeg,.png,.webp,.svg"
                                  />
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4 21.2852C3.71667 21.2852 3.47933 21.1892 3.288 20.9972C3.09667 20.8052 3.00067 20.5678 3 20.2852V17.8602C3 17.5935 3.05 17.3392 3.15 17.0972C3.25 16.8552 3.39167 16.6428 3.575 16.4602L16.2 3.86016C16.4 3.67682 16.621 3.53516 16.863 3.43516C17.105 3.33516 17.359 3.28516 17.625 3.28516C17.891 3.28516 18.1493 3.33516 18.4 3.43516C18.6507 3.53516 18.8673 3.68516 19.05 3.88516L20.425 5.28516C20.625 5.46849 20.7707 5.68516 20.862 5.93516C20.9533 6.18516 20.9993 6.43516 21 6.68516C21 6.95182 20.954 7.20616 20.862 7.44816C20.77 7.69016 20.6243 7.91082 20.425 8.11016L7.825 20.7102C7.64167 20.8935 7.429 21.0352 7.187 21.1352C6.945 21.2352 6.691 21.2852 6.425 21.2852H4ZM17.6 8.08516L19 6.68516L17.6 5.28516L16.2 6.68516L17.6 8.08516Z" fill="white"/>
                                  </svg>
                                </label>
                            </div>
                            </>
                          ) : (
                            <label className="flex flex-col rounded-2xl border border-[#6B7280] border-dashed items-center justify-center w-full sm:w-[378px] h-[269px] cursor-pointer" 
                                  style={{borderWidth: '1px', strokeDasharray: '4,4'}}>
                              <input
                                type="file"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  e.target.value = '';
                                  if (file) handleGalleryImageChange(index, file);
                                }}
                                className="hidden"
                                accept=".jpg,.jpeg,.png,.webp,.svg"
                              />
                              <svg width="124" height="124" viewBox="0 0 124 124" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M25.8333 108.5C22.9917 108.5 20.5599 107.489 18.538 105.467C16.5161 103.445 15.5034 101.012 15.5 98.1667V25.8333C15.5 22.9917 16.5127 20.5599 18.538 18.538C20.5633 16.5161 22.9951 15.5034 25.8333 15.5H98.1667C101.008 15.5 103.442 16.5127 105.467 18.538C107.492 20.5633 108.503 22.9951 108.5 25.8333V98.1667C108.5 101.008 107.489 103.442 105.467 105.467C103.445 107.492 101.012 108.503 98.1667 108.5H25.8333ZM25.8333 98.1667H98.1667V25.8333H25.8333V98.1667ZM31 87.8333H93L73.625 62L58.125 82.6667L46.5 67.1667L31 87.8333Z" fill="#6B7280" fillOpacity="0.3"/>
                                <g clipPath="url(#clip0_3789_80925)">
                                  <path d="M61.4969 86.9935C55.0066 86.9935 48.5164 86.9986 42.0261 86.9901C39.5322 86.9866 37.5739 85.2337 37.0828 82.595C37.0398 82.3684 37.0138 82.1316 37.0138 81.8999C37.0061 79.0312 36.9907 76.1625 37.0076 73.2938C37.0153 72.0962 37.7213 71.1269 38.7326 70.7913C39.7256 70.4608 40.763 70.8407 41.3938 71.7589C41.7713 72.3091 41.8834 72.936 41.8818 73.6157C41.8742 76.0381 41.8941 78.4605 41.868 80.8829C41.8619 81.423 42.0092 81.5507 42.4804 81.5507C55.1539 81.5371 67.8291 81.5354 80.5026 81.5541C81.0367 81.5541 81.1257 81.3599 81.1211 80.8267C81.0996 78.3192 81.0996 75.8116 81.1211 73.304C81.1319 72.1337 81.8117 71.161 82.7801 70.8083C83.7623 70.4506 84.8182 70.7998 85.4659 71.6993C85.825 72.1984 85.9923 72.7725 85.9923 73.413C85.9892 76.154 86.023 78.8966 85.98 81.6376C85.9324 84.6784 83.8222 86.9645 81.0843 86.9867C78.2896 87.0088 75.4934 86.9918 72.6987 86.9918C68.9647 86.9918 65.2323 86.9918 61.4984 86.9918L61.4969 86.9935Z" fill="#6B7280"/>
                                  <path d="M59.0527 46.7367C57.604 48.1421 56.2795 49.4283 54.9566 50.711C54.4087 51.2425 53.87 51.7842 53.3099 52.2987C52.2356 53.2885 50.7408 53.1794 49.8553 52.0653C48.959 50.9376 49.0234 49.2647 50.0716 48.2375C53.3436 45.0332 56.6294 41.8476 59.926 38.6757C60.8667 37.7694 62.1298 37.7762 63.0783 38.6876C66.3748 41.8595 69.6606 45.0451 72.9295 48.2529C73.9731 49.2767 74.0222 50.9734 73.1198 52.0841C72.222 53.1896 70.7241 53.2987 69.6637 52.2868C67.9233 50.6242 66.2044 48.9343 64.4733 47.2597C64.3336 47.1234 64.2201 46.9411 64.0067 46.89C63.8656 47.1217 63.9392 47.3824 63.9392 47.6225C63.9346 56.1197 63.9362 64.6168 63.9331 73.114C63.9331 74.78 63.1504 75.8584 61.7998 76.0832C60.4923 76.3013 59.2031 75.1957 59.0742 73.7341C59.0466 73.4172 59.0527 73.097 59.0527 72.7784C59.0527 64.409 59.0527 56.0396 59.0527 47.6685C59.0527 47.4198 59.0527 47.1711 59.0527 46.7367Z" fill="#6B7280"/>
                                </g>
                                <defs>
                                  <clipPath id="clip0_3789_80925">
                                    <rect width="49" height="49" fill="white" transform="translate(37 38)"/>
                                  </clipPath>
                                </defs>
                              </svg>
                            </label>
                          )}
                        </div>
                      </div>
                    ))}
                  </Slider>
                </div>
                <div className="flex items-center justify-center pt-16">
                    <div className="flex w-full md:w-[65%] gap-7 items-center justify-center">  
                        <SaveButton
                          onClick={handleSave}
                          loading={isLoading}
                          disabled={isLoading}
                        />
                        <CancelButton
                          onClick={handleCancel}
                          disabled={isLoading}
                        />
                    </div>
                </div>

              {/** 
                <h2 className="text-[#24325A] text-[32px] font-itcdemi">
                    Recomendaciones
                </h2>

                <NewsCard/>
              */}
            </div>
        </div>
      </div>
      <ModalAlertWarning
        showModalAlertWarning={showModalAlertWarning}
        closeModal={() => setShowModalAlertWarning(false)}
        error={modalMessage}
        accept={() => setShowModalAlertWarning(false)}
      />

      <ModalSuccess
        showModalSuccess={showModalSuccess}
        closeModalSuccess={() => setShowModalSuccess(false)}
        fill="green"
        text={modalMessage}
      />
    </div>
  );
}